import { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const TIMES = ["12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM"];

const FIELD_CLASS = "bg-[#141414] border-white/10 text-[#F5F5F5] placeholder:text-[#666666] focus-visible:ring-[#D98B48] focus-visible:border-[#D98B48] rounded-sm";
const today = new Date(new Date().setHours(0, 0, 0, 0));

export default function BookingForm({ packages, selectedPackage, onPackageChange }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_date: "", event_time: "",
    guest_count: "", venue: "", notes: "",
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selected = packages.find((p) => p.id === selectedPackage);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPackage) {
      toast.error("Choose a package first.");
      return;
    }
    if (!form.event_date) {
      toast.error("Pick your event date.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        ...form,
        guest_count: Number(form.guest_count),
        package_id: selectedPackage,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-24 md:py-32 bg-[#0C0C0C] border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal className="mb-14">
          <p className="eyebrow mb-5">Booking</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#F5F5F5]">
            Reserve your <em className="italic text-[#D98B48]">date</em>.
          </h2>
          <p className="mt-5 text-[#A3A3A3] font-light max-w-xl">
            Tell us the essentials. You'll pay the deposit on the next screen and
            we'll confirm your booking within 24 hours.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-3" delay={0.05}>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6" data-testid="booking-form">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Package</Label>
                <Select value={selectedPackage || ""} onValueChange={onPackageChange} required>
                  <SelectTrigger data-testid="booking-package-select" className={`w-full ${FIELD_CLASS} h-12`}>
                    <SelectValue placeholder="Choose a package" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 rounded-sm">
                    {packages.map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`booking-package-option-${p.id}`}>
                        {p.name} — from ${p.price.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Event date</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="booking-date-trigger"
                      className={`w-full justify-start text-left font-normal h-12 ${FIELD_CLASS}`}
                    >
                      <CalendarIcon className="h-4 w-4 mr-2 text-[#C5A059]" />
                      {form.event_date ? format(new Date(`${form.event_date}T12:00:00`), "MMMM d, yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0 bg-[#141414] border-white/10 rounded-sm">
                    <Calendar
                      mode="single"
                      selected={form.event_date ? new Date(`${form.event_date}T12:00:00`) : undefined}
                      onSelect={(d) => {
                        if (d) {
                          set("event_date", format(d, "yyyy-MM-dd"));
                          setDateOpen(false);
                        }
                      }}
                      disabled={(d) => d < today}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Bar opens at</Label>
                <Select value={form.event_time} onValueChange={(v) => set("event_time", v)} required>
                  <SelectTrigger data-testid="booking-time-select" className={`w-full ${FIELD_CLASS} h-12`}>
                    <SelectValue placeholder="Start time" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 rounded-sm max-h-64">
                    {TIMES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Guest count</Label>
                <Input
                  data-testid="booking-guests-input"
                  type="number" min="10" max="1000" required
                  value={form.guest_count}
                  onChange={(e) => set("guest_count", e.target.value)}
                  placeholder="e.g. 80"
                  className={`h-12 ${FIELD_CLASS}`}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Full name</Label>
                <Input
                  data-testid="booking-name-input" required
                  value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="Your name"
                  className={`h-12 ${FIELD_CLASS}`}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Email</Label>
                <Input
                  data-testid="booking-email-input" type="email" required
                  value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="you@email.com"
                  className={`h-12 ${FIELD_CLASS}`}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Phone</Label>
                <Input
                  data-testid="booking-phone-input" type="tel" required
                  value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  placeholder="(555) 000-0000"
                  className={`h-12 ${FIELD_CLASS}`}
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Venue address</Label>
                <Input
                  data-testid="booking-venue-input" required
                  value={form.venue} onChange={(e) => set("venue", e.target.value)}
                  placeholder="Where's the party?"
                  className={`h-12 ${FIELD_CLASS}`}
                />
              </div>

              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 block">Notes (optional)</Label>
                <Textarea
                  data-testid="booking-notes-input" rows={3}
                  value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  placeholder="Theme, dietary quirks, the cocktail you've been dreaming about…"
                  className={FIELD_CLASS}
                />
              </div>

              <div className="sm:col-span-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-button"
                  className="w-full rounded-sm bg-[#D98B48] hover:bg-[#F0A562] text-[#090909] font-medium h-14 text-base"
                >
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Taking you to checkout…
                    </span>
                  ) : selected ? (
                    `Reserve your date — pay $${selected.deposit} deposit`
                  ) : (
                    "Continue to deposit"
                  )}
                </Button>
              </div>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-2" delay={0.15}>
            <aside className="border border-white/10 bg-[#141414] p-8 lg:sticky lg:top-28" data-testid="booking-summary-card">
              <p className="eyebrow mb-6">Your reservation</p>
              {selected ? (
                <div>
                  <h3 className="font-display text-3xl text-[#F5F5F5]">{selected.name}</h3>
                  <p className="mt-1 italic font-display text-lg text-[#C5A059]">{selected.tagline}</p>
                  <dl className="mt-8 space-y-4 text-sm">
                    <div className="flex justify-between border-t border-white/10 pt-4">
                      <dt className="text-[#A3A3A3]">Package from</dt>
                      <dd className="text-[#F5F5F5]">${selected.price.toLocaleString()}</dd>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-4">
                      <dt className="text-[#A3A3A3]">Deposit today</dt>
                      <dd className="text-[#D98B48] font-medium" data-testid="summary-deposit-amount">${selected.deposit}</dd>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-4">
                      <dt className="text-[#A3A3A3]">Balance due</dt>
                      <dd className="text-[#F5F5F5]">On event day</dd>
                    </div>
                  </dl>
                </div>
              ) : (
                <p className="text-sm text-[#A3A3A3] font-light leading-relaxed">
                  Choose a package above or from the{" "}
                  <a href="#packages" className="text-[#D98B48] underline-offset-4 hover:underline">Packages</a>{" "}
                  section and your reservation summary will appear here.
                </p>
              )}
              <p className="mt-8 text-xs text-[#A3A3A3] font-light leading-relaxed border-t border-white/10 pt-6">
                Deposits are refundable up to 14 days before your event.
                A 10% hold fee will be accrued to reservations cancelled 72
                hours after booking.
              </p>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
